export type Project = {
  id: string;
  name: string;
  role: string;
  year: string;
  blurb: string;
  /** Longer line for the case-study / detail context. */
  detail: string;
  stack: string[];
  category: "Leading now" | "Industry" | "Personal" | "University";
  href?: string;
  /** Path under /public. Present only for projects with real imagery. */
  image?: string;
};

/**
 * Everything here is grounded in work Adi has actually shipped — no invented
 * claims. Projects with a real screenshot lead the visual gallery; the rest
 * fill out the full index.
 */
export const projects: Project[] = [
  {
    id: "vy-australia",
    name: "Vihangam Yog Australia",
    role: "Lead engineer",
    year: "2024 — now",
    blurb:
      "Free guided meditation in Sydney and online, taught in plain language.",
    detail:
      "An ancient meditation, taught plainly — ten minutes in the morning and ten in the evening. I lead the site and the platform behind its free guided sessions.",
    stack: ["React", "TypeScript", "Cloudflare"],
    category: "Leading now",
    href: "https://vyaustralia.org/",
    image: "/images/projects/vy-australia.jpeg",
  },
  {
    id: "evergrowing",
    name: "Evergrowing",
    role: "Lead engineer",
    year: "2025 — now",
    blurb: "A twice-a-day meditation companion — web and mobile.",
    detail:
      "A companion for daily practice from Vihangam Yog: a timer, a sense of growth, talks and community — a whole practice, quietly held across web and a native app.",
    stack: ["React", "Expo", "Cloudflare Worker"],
    category: "Leading now",
    href: "https://evergrowing-web.pages.dev/",
    image: "/images/projects/evergrowing.jpeg",
  },
  {
    id: "vy-nz",
    name: "Vihangam Yog New Zealand",
    role: "Lead engineer",
    year: "2024 — now",
    blurb: "The same practice, free across Aotearoa and online.",
    detail:
      "The New Zealand home for the same practice — free guided sessions across Aotearoa and online, built on the platform I lead.",
    stack: ["React", "TypeScript", "Cloudflare"],
    category: "Leading now",
    href: "https://vihangamyognz.org/",
    image: "/images/projects/vy-nz.jpeg",
  },
  {
    id: "vir-srinivas",
    name: "Vir Srinivas",
    role: "Design & development",
    year: "2021 — now",
    blurb: "A cinematic site where each film is its own world.",
    detail:
      "A ground-up rebuild for an award-winning writer, director and producer. Every film becomes its own visual world, drawn from its own footage and colour, so the site feels like the work rather than describing it.",
    stack: ["React", "TypeScript", "Next.js", "Vercel"],
    category: "Industry",
    href: "https://www.virsrinivas.com/",
    image: "/images/projects/vir-srinivas.jpeg",
  },
  {
    id: "sadafal-education",
    name: "Sadafal Education",
    role: "Full-stack developer, UI designer",
    year: "2020 — now",
    blurb:
      "A global home for curiosity — discover, learn and master your passion.",
    detail:
      "A full redesign and build for an aspiring global education provider, plus an e-commerce experience for student-made products.",
    stack: ["React", "TypeScript", "Remix", "GraphQL"],
    category: "Industry",
    href: "https://sadafal-education.com",
    image: "/images/projects/sadafal-education.jpeg",
  },
  {
    id: "neuronudge",
    name: "NeuroNudge",
    role: "Creator",
    year: "2023",
    blurb: "Adaptive learning for unique minds.",
    detail:
      "An educational platform with adaptive Q-learning for individuals with ASD — personalised learning journeys and interactive challenges.",
    stack: ["Rust"],
    category: "University",
    href: "https://github.com/kishek2000/neuro-nudge",
    image: "/images/projects/neuronudge.webp",
  },
  {
    id: "bahudha",
    name: "Bahudha Foundation",
    role: "Full-stack developer",
    year: "2022 — now",
    blurb: "A calm public home for global peace and harmony.",
    detail:
      "A website for a foundation focused on empowerment, cultural exchange and a vision for global peace and harmony.",
    stack: ["React", "TypeScript", "Cloudflare"],
    category: "Industry",
    href: "https://bahudha-foundation.org",
    image: "/images/projects/bahudha.jpeg",
  },
  {
    id: "galacticed",
    name: "GalacticEd",
    role: "Full-stack developer",
    year: "2021 — now",
    blurb: "Tailored learning for children with ASD.",
    detail:
      "An intelligent education platform that tailors content dynamically for children with ASD — built with Autism Awareness and Accenture.",
    stack: ["React", "TypeScript", "Apollo GraphQL", "MongoDB"],
    category: "Industry",
  },
  {
    id: "dungeon-mania",
    name: "Dungeon Mania",
    role: "Developer",
    year: "2022",
    blurb: "Explore dungeons and defeat monsters.",
    detail:
      "A 2D game with time travel, potions and a pile of interactive gameplay elements.",
    stack: ["Java"],
    category: "University",
  },
  {
    id: "concentrum",
    name: "Concentrum",
    role: "Developer",
    year: "2021",
    blurb: "A disease-outbreak information platform.",
    detail:
      "A widget library and customisable dashboards for disease-outbreak information, with sharing built in.",
    stack: ["React", "TypeScript", "FastAPI"],
    category: "University",
  },
  {
    id: "transport-me",
    name: "transport.me",
    role: "Developer & designer",
    year: "2020",
    blurb: "All your travel needs in one place.",
    detail:
      "A mobile app that removes the need for multiple apps, managing all your travel needs in one place.",
    stack: ["React", "Expo", "Figma"],
    category: "University",
  },
  {
    id: "knowledge-path",
    name: "knowledge.path",
    role: "Developer",
    year: "2020 — now",
    blurb: "Anyone can learn. Powered by Sadafal Education.",
    detail:
      "An education platform where anyone can be employed in their passion, powered by Sadafal Education.",
    stack: ["TypeScript", "GraphQL", "DynamoDB"],
    category: "Personal",
  },
  {
    id: "discovery",
    name: "Discovery",
    role: "Developer & designer",
    year: "2020",
    blurb: "Learning made fun.",
    detail:
      "A platform that makes children's education fun and engaging, covering interesting areas of the world.",
    stack: ["React", "Figma"],
    category: "Personal",
  },
  {
    id: "mylounge",
    name: "myLounge",
    role: "Developer",
    year: "2020",
    blurb: "Listen. Watch. Relax.",
    detail:
      "A digital lounge to browse TV shows, movies, podcasts and music, finding streaming services for any media instantly.",
    stack: ["React", "Django", "CSS"],
    category: "Personal",
  },
  {
    id: "knowledge-discovery",
    name: "Sadafal Commerce",
    role: "Full-stack developer",
    year: "2020 — now",
    blurb: "An e-commerce home for student-made products.",
    detail:
      "The commerce side of Sadafal Education — a storefront for products made by students learning their craft.",
    stack: ["React", "TypeScript", "CosmosDB"],
    category: "Industry",
  },
];

export const galleryProjects = projects.filter((p) => p.image);
